import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import _ from "lodash";
import PropTypes from "prop-types";
import i18n from "i18next";

import { Panel, PanelMobile } from "../Layout/Panel";
import { SortedList, SortedListPagination } from "../Layout/SortedList";
import { filterProducts } from "./Filters/FilterHelpers";
import { formatDimension } from "../../helpers/unitOfMeasurementHelpers";
import { getBillOfLadingDocuments, getBillsOfLading, getCustomer, getPurchaseOrder, getPurchaseOrderLoads } from "../../actions";
import { getBillsOfLadingSelector, getIsLoadingSelector, getLoadSelector } from "../../selectors/selectors";
import { searchColumns as productsSearchColumns, sortColumns as productsSortColumns } from "./List/Products/ProductsListColumns";
import { searchColumns as rollsSearchColumns, sortColumns as rollsSortColumns } from "./List/Rolls/RollsListColumns";
import { withNavigationHistory } from "../Common/Navigation/withNavigationHistory";
import Document from "../Common/Document/Document";
import Filters from "./Filters/Filters";
import IdTypes from "./IdTypes";
import PanelTitle from "./PanelTitle";
import PanelTools from "./../Layout/Panel/PanelTools";
import ProductsListHeader from "./List/Products/ProductsListHeader";
import ProductsListRow from "./List/Products/ProductsListRow";
import RollsListHeader from "./List/Rolls/RollsListHeader";
import RollsListRow from "./List/Rolls/RollsListRow";
import RollsListRowTotals from "./List/Rolls/RollsListRowTotals";
import Routing from "./Routing/Routing";
import SidePanel from "./../Layout/Panel/SidePanel";
import SubNav from "./SubNav";
import Summary from "./Summary";
import SummaryMobile from "./SummaryMobile";
import ViewTypes from "./ViewTypes";

class DetailsPage extends PureComponent {
  static propTypes = {
    getBillOfLadingDocuments: PropTypes.func.isRequired,
    getBillsOfLading: PropTypes.func.isRequired,
    getCustomer: PropTypes.func.isRequired,
    getPurchaseOrder: PropTypes.func.isRequired,
    getPurchaseOrderLoads: PropTypes.func.isRequired,
    billsOfLading: PropTypes.array,
    billsOfLadingById: PropTypes.object,
    customerId: PropTypes.string,
    customerPOId: PropTypes.string,
    isDocumentLoading: PropTypes.bool,
    isLoading: PropTypes.bool,
    isMobile: PropTypes.bool,
    load: PropTypes.object,
    loadId: PropTypes.string,
    shipmentId: PropTypes.string,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    billsOfLading: [],
    billsOfLadingById: {},
    customerId: "",
    customerPOId: "",
    isDocumentLoading: false,
    isLoading: false,
    isMobile: false,
    load: {},
    loadId: "",
    shipmentId: "",
    userMetadata: {}
  };

  state = {
    currentRolls: null,
    isRoutingVisible: false,
    isDocumentVisible: false,
    isSummaryOpen: false,
    viewState: {
      isProductSelected: true,
      selectedBillOfLadingId: this.props.shipmentId,
      selectedCustomerPO: null,
      selectedDimension: null,
      selectedProductName: null,
      selectedRollKeyBy: IdTypes.tappiId,
      products: {
        currentPage: 1,
        searchQuery: "",
        sort: {}
      },
      rolls: {
        currentPage: 1,
        searchQuery: "",
        sort: {}
      }
    }
  };

  componentDidMount() {
    if (this.props.customerId) {
      this.props.getCustomer(this.props.customerId);
    }

    if (this.props.customerPOId) {
      this.props.getPurchaseOrder(this.props.customerPOId, {});
      this.props.getPurchaseOrderLoads(this.props.customerPOId, {});
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { products = [] } = this.selectedBillOfLading;
    const { selectedCustomerPO, selectedDimension, selectedProductName, selectedBillOfLadingId } = this.state.viewState;

    const hasSelectedCustomerPOChanged = selectedCustomerPO !== prevState.viewState.selectedCustomerPO;
    const hasSelectedDimensionChanged = selectedDimension !== prevState.viewState.selectedDimension;
    const hasSelectedProductNameChanged = selectedProductName !== prevState.viewState.selectedProductName;
    const hasSelectedBillsOfLadingChanged = selectedBillOfLadingId !== prevState.viewState.selectedBillOfLadingId;
    const hasBillsOfLadingChanged = this.props.billsOfLading !== prevProps.billsOfLading;

    if (
      hasSelectedCustomerPOChanged ||
      hasSelectedDimensionChanged ||
      hasSelectedProductNameChanged ||
      hasSelectedBillsOfLadingChanged ||
      hasBillsOfLadingChanged
    ) {
      const filteredProducts = filterProducts(products, selectedCustomerPO, selectedDimension, selectedProductName);
      this.setCurrentRolls(filteredProducts);
    }
  }

  get selectedBillOfLading() {
    return this.props.billsOfLadingById[this.state.viewState.selectedBillOfLadingId] || _.first(this.props.billsOfLading) || {};
  }

  get currentRolls() {
    const { products = [] } = this.selectedBillOfLading;
    const updatedProducts = products.map(product =>
      product.rolls.map(roll => ({
        ...roll,
        productName: product.productName,
        dimension: product.dimension
      }))
    );

    if (!_.isEmpty(this.state.currentRolls)) {
      return this.state.currentRolls;
    }

    return _.flatten(updatedProducts) || [];
  }

  setCurrentRolls = products => {
    const currentRolls = products.map(product =>
      product.rolls.map(roll => ({ productName: product.productName, dimension: product.dimension, ...roll }))
    );

    this.setState({ currentRolls: _.flatten(currentRolls), isSummaryOpen: false });
  };

  handleGetDocuments = () => {
    const { id, invoices = {} } = this.selectedBillOfLading;
    return this.props.getBillOfLadingDocuments(id, invoices.id);
  };

  handleSetViewState = item => this.setState({ viewState: { ...this.state.viewState, ...item } });

  handleSetSubnavViewState = item => {
    const { isProductSelected, products, rolls } = this.state.viewState;
    const keyName = isProductSelected ? "products" : "rolls";
    const value = isProductSelected ? products : rolls;

    this.handleSetViewState({ [keyName]: { ...value, ...item } });
  };

  handleChangeSelectedRollKeyBy = () => {
    const selectedRollKeyBy = this.state.viewState.selectedRollKeyBy === IdTypes.tappiId ? IdTypes.ifraId : IdTypes.tappiId;
    this.handleSetViewState({ selectedRollKeyBy });
  };

  handleSearch = searchQuery => this.handleSetSubnavViewState({ searchQuery, currentPage: 1 });

  handleChangePagination = currentPage => this.handleSetSubnavViewState({ currentPage });

  handleChangeSort = sort => this.handleSetSubnavViewState({ sort });

  handleSelectCurrentBillOfLading = id => {
    this.setState({ currentRolls: null });
    this.handleSetViewState({ selectedCustomerPO: null, selectedDimension: null, selectedProductName: null, selectedBillOfLadingId: id });
  };

  handleToggleSubnav = isProductSelected => {
    this.setState({ currentRolls: null });
    this.handleSetViewState({ selectedCustomerPO: null, selectedDimension: null, selectedProductName: null, isProductSelected });
  };

  handleRollsClick = product => {
    this.handleSetViewState({
      selectedCustomerPO: product.customerPO,
      selectedDimension: formatDimension(product.dimension),
      selectedProductName: product.productName,
      isProductSelected: false
    });
  };

  handleChangeUnits = units => this.props.getBillsOfLading(this.props.loadId, units);

  handleHideDocumentButton = () => this.setState({ isDocumentVisible: false });

  handleShowDocumentButton = () => this.setState({ isDocumentVisible: true });

  handleShowRoutingButton = () => this.setState({ isRoutingVisible: true });

  handleHideRoutingButton = () => this.setState({ isRoutingVisible: false });

  handleFilterChange = (fieldName, value) => this.handleSetViewState({ [fieldName]: value });

  handleSummaryButtonClick = () => {
    this.setState({ isSummaryOpen: !this.state.isSummaryOpen });
  };

  render() {
    const { billsOfLading, isDocumentLoading, isLoading, isMobile, load, userMetadata } = this.props;
    const { isDocumentVisible, isRoutingVisible, isSummaryOpen } = this.state;
    const { rolls, isProductSelected, selectedCustomerPO, selectedDimension, selectedProductName, selectedRollKeyBy } = this.state.viewState;
    const { currentPage, searchQuery, sort } = isProductSelected ? this.state.viewState.products : rolls;
    const activeSubNav = isProductSelected ? ViewTypes.Products : ViewTypes.Rolls;
    const { selectedBillOfLading } = this;

    return (
      <Fragment>
        <SidePanel position="right" isVisible={isDocumentVisible} panelVisibility={this.handleHideDocumentButton} isMobile={isMobile}>
          {isDocumentVisible ? (
            <Document
              noResultTitle={i18n.t("table.noBillsOfLadingDocumentsTitle")}
              noResultMessage={i18n.t("table.noBillsOfLadingDocumentsMessage")}
              getDocuments={this.handleGetDocuments}
              isLoading={isDocumentLoading}
            />
          ) : null}
        </SidePanel>
        <SidePanel position="right" isVisible={isRoutingVisible} panelVisibility={this.handleHideRoutingButton} isMobile={isMobile}>
          {isRoutingVisible ? <Routing selectedBillOfLading={selectedBillOfLading} isMobile={isMobile} /> : null}
        </SidePanel>
        {isMobile ? (
          <PanelMobile className="details-page">
            <PanelTitle
              billsOfLading={billsOfLading}
              onSelectCurrentBillOfLading={this.handleSelectCurrentBillOfLading}
              selectedBillOfLading={selectedBillOfLading}
              documentClick={this.handleShowDocumentButton}
              routingClick={this.handleShowRoutingButton}
              isMobile={isMobile}
              summaryButtonClick={this.handleSummaryButtonClick}
              summaryIsLoading
            />
            <SummaryMobile
              selectedBillOfLading={selectedBillOfLading}
              load={load}
              userMetadata={userMetadata}
              isSummaryOpen={isSummaryOpen}
              summaryButtonClick={this.handleSummaryButtonClick}
            />

            <SubNav activeSubNav={activeSubNav} onToggleOrderView={this.handleToggleSubnav} />

            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />

            {!isProductSelected && (
              <Filters
                billOfLading={selectedBillOfLading}
                onSubmit={this.handleFilterChange}
                rolls={this.currentRolls}
                selectedCustomerPO={selectedCustomerPO}
                selectedDimension={selectedDimension}
                selectedProductName={selectedProductName}
                selectedRollKeyBy={selectedRollKeyBy}
                userMetadata={userMetadata}
              />
            )}

            {isProductSelected && (
              <SortedList
                RowComponent={ProductsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ProductsListHeader}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                currentPage={currentPage}
                data={selectedBillOfLading.products}
                isLoading={isLoading}
                isMobile={isMobile}
                headerProps={{ selectedKeyById: "id" }}
                rowProps={{ keyBy: "id", onRollsClick: this.handleRollsClick, userMetadata }}
                searchQuery={searchQuery}
                searchValues={productsSearchColumns}
                sort={sort}
                sortValues={productsSortColumns}
              />
            )}

            {!isProductSelected && (
              <SortedList
                RowComponent={RollsListRow}
                RowComponentTotals={RollsListRowTotals}
                PaginationComponent={SortedListPagination}
                HeaderComponent={RollsListHeader}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                currentPage={currentPage}
                data={this.currentRolls}
                isLoading={isLoading}
                isMobile={isMobile}
                headerProps={{ selectedKeyById: selectedRollKeyBy, onDropdownClick: this.handleChangeSelectedRollKeyBy }}
                rowProps={{ keyBy: selectedRollKeyBy, userMetadata }}
                searchQuery={searchQuery}
                searchValues={rollsSearchColumns}
                sort={sort}
                sortValues={rollsSortColumns}
              />
            )}
          </PanelMobile>
        ) : (
          <Panel className="details-page">
            <PanelTitle
              billsOfLading={billsOfLading}
              onSelectCurrentBillOfLading={this.handleSelectCurrentBillOfLading}
              selectedBillOfLading={selectedBillOfLading}
              documentClick={this.handleShowDocumentButton}
              routingClick={this.handleShowRoutingButton}
              isMobile={isMobile}
              summaryButtonClick={this.handleSummaryButtonClick}
              summaryIsLoading
            />
            <Summary selectedBillOfLading={selectedBillOfLading} load={load} userMetadata={userMetadata} />
            <SubNav activeSubNav={activeSubNav} onToggleOrderView={this.handleToggleSubnav} />
            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />
            {!isProductSelected && (
              <Filters
                billOfLading={selectedBillOfLading}
                onSubmit={this.handleFilterChange}
                rolls={this.currentRolls}
                selectedCustomerPO={selectedCustomerPO}
                selectedDimension={selectedDimension}
                selectedProductName={selectedProductName}
                selectedRollKeyBy={selectedRollKeyBy}
                userMetadata={userMetadata}
              />
            )}
            {isProductSelected && (
              <SortedList
                RowComponent={ProductsListRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ProductsListHeader}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                currentPage={currentPage}
                data={selectedBillOfLading.products}
                isLoading={isLoading}
                headerProps={{ selectedKeyById: "id" }}
                rowProps={{ keyBy: "id", onRollsClick: this.handleRollsClick, userMetadata }}
                searchQuery={searchQuery}
                searchValues={productsSearchColumns}
                sort={sort}
                sortValues={productsSortColumns}
              />
            )}
            {!isProductSelected && (
              <SortedList
                RowComponent={RollsListRow}
                RowComponentTotals={RollsListRowTotals}
                PaginationComponent={SortedListPagination}
                HeaderComponent={RollsListHeader}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                currentPage={currentPage}
                data={this.currentRolls}
                isLoading={isLoading}
                headerProps={{ selectedKeyById: selectedRollKeyBy, onDropdownClick: this.handleChangeSelectedRollKeyBy }}
                rowProps={{ keyBy: selectedRollKeyBy, userMetadata }}
                searchQuery={searchQuery}
                searchValues={rollsSearchColumns}
                sort={sort}
                sortValues={rollsSortColumns}
              />
            )}
          </Panel>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { loadId, shipmentId } = state.app.routerParams;
  const billsOfLading = getBillsOfLadingSelector(state);

  return {
    billsOfLading,
    billsOfLadingById: state.billsOfLading.billsOfLadingById,
    customerId: state.app.routerParams.customerId,
    customerPOId: state.app.routerParams.customerPOId,
    isLoading: getIsLoadingSelector(state, [getBillsOfLading.id, getPurchaseOrderLoads.id]),
    isDocumentLoading: getIsLoadingSelector(state, [getBillOfLadingDocuments.id]),
    isMobile: state.app.isMobile,
    load: getLoadSelector(state, loadId),
    loadId,
    shipmentId,
    userMetadata: state.app.userMetadata
  };
};

export default connect(
  mapStateToProps,
  { getBillOfLadingDocuments, getBillsOfLading, getPurchaseOrderLoads, getPurchaseOrder, getCustomer }
)(withNavigationHistory(DetailsPage));



// WEBPACK FOOTER //
// ./src/components/BillOfLading/DetailsPage.js